import { createPortal } from 'react-dom';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { api } from '../api/apiClient';
import Breadcrumb from '../ui/Breadcrumb';
import Modal from '../ui/Modal';
import Skeleton from '../ui/Skeleton';
import { moduleColor } from '../ui/modulePalette';
import { IconAlertTriangle, IconCheck, IconDownload, IconEdit, IconFolder, IconImage, IconPlus, IconSparkles, IconTrash, IconVideo } from '../ui/icons';

function formatBytes(n) {
  const v = Number(n || 0);
  if (!Number.isFinite(v) || v <= 0) return '—';
  const units = ['B', 'KB', 'MB', 'GB'];
  let i = 0;
  let x = v;
  while (x >= 1024 && i < units.length - 1) {
    x /= 1024;
    i++;
  }
  return `${x.toFixed(i === 0 ? 0 : 1)} ${units[i]}`;
}

function useApiBaseUrl() {
  const raw = process.env.REACT_APP_API_BASE_URL;
  if (raw && raw.trim()) return raw.replace(/\/+$/, '');
  return '/api';
}

function useInViewOnce({ rootMargin = '400px' } = {}) {
  const ref = useRef(null);
  const [inView, setInView] = useState(false);

  useEffect(() => {
    if (inView) return;
    const el = ref.current;
    if (!el) return;
    if (typeof window === 'undefined' || typeof IntersectionObserver === 'undefined') {
      setInView(true);
      return;
    }
    const obs = new IntersectionObserver(
      (entries) => {
        const hit = entries.some((e) => e && e.isIntersecting);
        if (hit) setInView(true);
      },
      { root: null, rootMargin, threshold: 0.01 }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [inView, rootMargin]);

  return { ref, inView };
}

function MediaThumbImage({ src, alt, eager = false }) {
  const [loaded, setLoaded] = useState(false);
  const { ref, inView } = useInViewOnce({ rootMargin: '500px' });
  const shouldLoad = Boolean((eager || inView) && src);
  return (
    <div className="MediaThumbWrap" ref={ref}>
      {shouldLoad && !loaded ? <span className="Spinner MediaThumbSpinner" aria-hidden="true" /> : null}
      {shouldLoad ? (
        <img
          src={src}
          alt={alt}
          className="MediaThumb MediaThumb--contain"
          loading="lazy"
          decoding="async"
          fetchPriority="low"
          onLoad={() => setLoaded(true)}
          onError={() => setLoaded(true)}
        />
      ) : (
        <div className="MediaThumbPlaceholder" aria-hidden="true">
          <IconImage />
        </div>
      )}
    </div>
  );
}

function MediaThumbVideoPlaceholder() {
  return (
    <div className="MediaThumbWrap">
      <div className="MediaThumbPlaceholder" aria-hidden="true">
        <IconVideo />
      </div>
    </div>
  );
}

function normalizeFolderName(name) {
  return String(name || '')
    .trim()
    .toLowerCase();
}

function canConvertToWebp(asset) {
  return asset?.kind === 'image' && String(asset?.mime_type || '') !== 'image/webp' && String(asset?.variant || '') === 'original';
}

function FixedContextMenu({ menu, onClose }) {
  const ref = useRef(null);

  useEffect(() => {
    if (!menu) return;
    function onKey(e) {
      if (e.key === 'Escape') onClose?.();
    }
    function onDown(e) {
      const el = ref.current;
      if (!el) return;
      if (el.contains(e.target)) return;
      onClose?.();
    }
    window.addEventListener('keydown', onKey);
    window.addEventListener('mousedown', onDown);
    window.addEventListener('scroll', onClose, true);
    window.addEventListener('resize', onClose);
    return () => {
      window.removeEventListener('keydown', onKey);
      window.removeEventListener('mousedown', onDown);
      window.removeEventListener('scroll', onClose, true);
      window.removeEventListener('resize', onClose);
    };
  }, [menu, onClose]);

  if (!menu || typeof document === 'undefined') return null;
  return createPortal(
    <div
      ref={ref}
      className="Menu Menu--fixed"
      style={{ position: 'fixed', left: Number(menu.x) || 0, top: Number(menu.y) || 0 }}
      role="menu"
    >
      {(Array.isArray(menu.items) ? menu.items : []).map((it, idx) => {
        if (it?.type === 'sep') return <div key={`sep_${idx}`} className="MenuSeparator" role="separator" />;
        return (
          <button
            key={it.key || `it_${idx}`}
            type="button"
            className={`MenuItem ${it.variant === 'danger' ? 'MenuItem--danger' : ''}`}
            role="menuitem"
            disabled={Boolean(it.disabled)}
            onClick={() => {
              if (it.disabled) return;
              onClose?.();
              it.onClick?.();
            }}
          >
            {it.label}
          </button>
        );
      })}
    </div>,
    document.body
  );
}

function defaultProjectContent() {
  return {
    version: 1,
    meta: { description: '', summary_lines: ['', '', ''] },
    sections: [],
  };
}

export default function ResourcesDrivePage({ siteId, site, modules, panelSettings }) {
  const apiBase = useApiBaseUrl();
  const [path, setPath] = useState([{ type: 'root', label: 'Ressources' }]);
  const current = path[path.length - 1] || { type: 'root', label: 'Ressources' };
  const fileInputRef = useRef(null);
  const dragCounter = useRef(0);
  const dragMoveRef = useRef(null);
  const lastSelectedRef = useRef(null);

  const [foldersState, setFoldersState] = useState({ status: 'idle', error: '', folders: [] });
  const [allFoldersState, setAllFoldersState] = useState({ status: 'idle', error: '', folders: [] });
  const [assetsState, setAssetsState] = useState({ status: 'idle', error: '', assets: [] });
  const [projectsState, setProjectsState] = useState({ status: 'idle', error: '', projects: [] });
  const [modal, setModal] = useState(null);
  const [preview, setPreview] = useState(null); // { asset }
  const [dragActive, setDragActive] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [zipDownloading, setZipDownloading] = useState(false);
  const [zipError, setZipError] = useState('');
  const [dropFolderId, setDropFolderId] = useState(null);
  const [includeSubfolders, setIncludeSubfolders] = useState(false);
  const [dragCrumbKey, setDragCrumbKey] = useState(null);
  const [invalidDropKey, setInvalidDropKey] = useState(null);
  const [ctxMenu, setCtxMenu] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [typeFilter, setTypeFilter] = useState('all');
  const [mixMode, setMixMode] = useState('mixed');
  const [sortKey, setSortKey] = useState('name_asc');
  const invalidDropTimerRef = useRef(null);

  const projectsEnabled = useMemo(() => (modules || []).some((m) => m.key === 'projects' && !m.coming_soon), [modules]);
  const preloadImages = Boolean(panelSettings?.modules?.preload_images);

  const projectsByName = useMemo(() => {
    const set = new Set();
    for (const p of projectsState.projects || []) {
      const k = normalizeFolderName(p?.name);
      if (k) set.add(k);
    }
    return set;
  }, [projectsState.projects]);

  function triggerInvalidDrop(key) {
    const k = String(key || '');
    if (!k) return;
    if (invalidDropTimerRef.current) clearTimeout(invalidDropTimerRef.current);
    setInvalidDropKey(null);
    requestAnimationFrame(() => setInvalidDropKey(k));
    invalidDropTimerRef.current = setTimeout(() => setInvalidDropKey(null), 520);
  }

  useEffect(() => {
    return () => {
      if (invalidDropTimerRef.current) clearTimeout(invalidDropTimerRef.current);
    };
  }, []);

  const breadcrumbItems = (() => {
    function crumbKey(seg, idx) {
      if (seg?.type === 'folder') return `crumb_folder_${Number(seg.folderId) || idx}`;
      if (seg?.type === 'project') return `crumb_project_${Number(seg.projectId) || idx}`;
      return `crumb_${String(seg?.type || 'root')}_${idx}`;
    }

    function isDroppableCrumb(seg) {
      const t = String(seg?.type || '');
      return t === 'root' || t === 'folder';
    }

    function destFolderIdForCrumb(seg) {
      if (seg?.type === 'root') return null;
      if (seg?.type === 'folder') return Number(seg.folderId);
      return null;
    }

    return path.map((seg, idx) => {
      const key = crumbKey(seg, idx);
      const droppable = isDroppableCrumb(seg);
      return {
        key,
        label: seg.label,
        className:
          dragCrumbKey === key
            ? 'is-drop-target'
            : invalidDropKey === key
            ? 'is-drop-invalid'
            : '',
        onClick: idx < path.length - 1 ? () => setPath((p) => p.slice(0, idx + 1)) : undefined,
        onDragOver: (e) => {
          const ids = resolveDraggedMediaIds(e);
          if (!ids.length) return;
          e.preventDefault();
          e.stopPropagation();
          if (!droppable) {
            try {
              e.dataTransfer.dropEffect = 'none';
            } catch {
              // ignore
            }
            triggerInvalidDrop(key);
            setDragCrumbKey(null);
            return;
          }
          try {
            e.dataTransfer.dropEffect = 'move';
          } catch {
            // ignore
          }
          setDragCrumbKey(key);
        },
        onDragLeave: () => {
          if (dragCrumbKey === key) setDragCrumbKey(null);
        },
        onDrop: async (e) => {
          const ids = resolveDraggedMediaIds(e);
          if (!ids.length) return;
          e.preventDefault();
          e.stopPropagation();
          setDragCrumbKey(null);
          dragMoveRef.current = null;
          if (!droppable) {
            triggerInvalidDrop(key);
            return;
          }
          const destFolderId = destFolderIdForCrumb(seg);
          await moveMediaToFolder(ids, destFolderId);
          clearSelection();
        },
      };
    });
  })();

  const isClassicContext = current.type === 'root' || current.type === 'folder';
  const isProjectsContext = current.type === 'projects';
  const isProjectFolderContext = current.type === 'project';

  const folders = useMemo(() => (Array.isArray(foldersState.folders) ? foldersState.folders : []), [foldersState.folders]);
  const assets = useMemo(() => (Array.isArray(assetsState.assets) ? assetsState.assets : []), [assetsState.assets]);
  const projects = useMemo(() => (Array.isArray(projectsState.projects) ? projectsState.projects : []), [projectsState.projects]);

  const assetUrl = useCallback(
    (assetId, asDownload) =>
      `${apiBase}/media.php?site_id=${encodeURIComponent(String(siteId || 0))}&id=${encodeURIComponent(String(assetId))}${asDownload ? '&download=1' : ''}`,
    [apiBase, siteId]
  );

  const downloadZipUrl = useCallback(
    (ids) => {
      const list = (ids || []).map((x) => Number(x)).filter((n) => Number.isFinite(n) && n > 0);
      const qs = new URLSearchParams({
        site_id: String(Number(siteId || 0)),
        ids: list.join(','),
        download: '1',
      });
      return `${apiBase}/media.php?${qs.toString()}`;
    },
    [apiBase, siteId]
  );

  function filenameFromContentDisposition(headerValue) {
    const raw = String(headerValue || '').trim();
    if (!raw) return '';
    const star = raw.match(/filename\*\s*=\s*UTF-8''([^;]+)/i);
    if (star && star[1]) {
      try {
        return decodeURIComponent(star[1].trim().replace(/^"|"$/g, ''));
      } catch {
        return star[1].trim().replace(/^"|"$/g, '');
      }
    }
    const m = raw.match(/filename\s*=\s*([^;]+)/i);
    if (!m || !m[1]) return '';
    return String(m[1]).trim().replace(/^"|"$/g, '');
  }

  const rootSpecialFolders = useMemo(() => {
    if (current.type !== 'root') return [];
    const list = [];
    if (projectsEnabled) list.push({ type: 'projects', label: 'Projets' });
    return list;
  }, [current.type, projectsEnabled]);

  function folderAccent(folderName) {
    const k = normalizeFolderName(folderName);
    if (k && projectsByName.has(k)) return moduleColor('projects');
    return '';
  }

  useEffect(() => {
    if (current.type !== 'folder') {
      setIncludeSubfolders(false);
      return;
    }
    const accent = folderAccent(current.label);
    setIncludeSubfolders(Boolean(accent));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [current.type, current.folderId, projectsByName]);

  const refreshProjects = useCallback(() => {
    if (!projectsEnabled || !siteId) return;
    let cancelled = false;
    setProjectsState((s) => ({ ...(s || {}), status: 'loading', error: '' }));
    api
      .projectsList(Number(siteId))
      .then((res) => {
        if (cancelled) return;
        if (!res.ok) {
          setProjectsState({ status: 'error', error: res.error || 'Impossible de charger', projects: [] });
          return;
        }
        setProjectsState({ status: 'ready', error: '', projects: res.projects || [] });
      })
      .catch(() => {
        if (cancelled) return;
        setProjectsState({ status: 'error', error: 'Impossible de charger', projects: [] });
      });
    return () => {
      cancelled = true;
    };
  }, [projectsEnabled, siteId]);

  const refreshClassic = useCallback(() => {
    if (!siteId) return;
    let cancelled = false;
    setFoldersState({ status: 'loading', error: '', folders: [] });
    setAssetsState({ status: 'loading', error: '', assets: [] });
    api
      .mediaFolders(Number(siteId), { parent_id: current.type === 'folder' ? Number(current.folderId) : null })
      .then((res) => {
        if (cancelled) return;
        if (!res.ok) {
          setFoldersState({ status: 'error', error: res.error || 'Impossible de charger', folders: [] });
          return;
        }
        setFoldersState({ status: 'ready', error: '', folders: res.folders || [] });
      })
      .catch(() => {
        if (cancelled) return;
        setFoldersState({ status: 'error', error: 'Impossible de charger', folders: [] });
      });

    api
      .mediaListScoped(Number(siteId), {
        folder_id: current.type === 'folder' && !includeSubfolders ? Number(current.folderId) : null,
        limit: 500,
      })
      .then((res) => {
        if (cancelled) return;
        if (!res.ok) {
          setAssetsState({ status: 'error', error: res.error || 'Impossible de charger', assets: [] });
          return;
        }
        setAssetsState({ status: 'ready', error: '', assets: res.assets || [] });
      })
      .catch(() => {
        if (cancelled) return;
        setAssetsState({ status: 'error', error: 'Impossible de charger', assets: [] });
      });

    return () => {
      cancelled = true;
    };
  }, [siteId, current.type, current.folderId, includeSubfolders]);

  const refreshAllFolders = useCallback(() => {
    if (!siteId) return;
    let cancelled = false;
    setAllFoldersState((s) => ({ ...(s || {}), status: 'loading', error: '' }));
    api
      .mediaFolders(Number(siteId), { all: true })
      .then((res) => {
        if (cancelled) return;
        if (!res.ok) {
          setAllFoldersState({ status: 'error', error: res.error || 'Impossible de charger', folders: [] });
          return;
        }
        setAllFoldersState({ status: 'ready', error: '', folders: res.folders || [] });
      })
      .catch(() => {
        if (cancelled) return;
        setAllFoldersState({ status: 'error', error: 'Impossible de charger', folders: [] });
      });
    return () => {
      cancelled = true;
    };
  }, [siteId]);

  const refreshProjectFolder = useCallback(() => {
    if (!siteId) return;
    const pid = Number(current.projectId);
    if (!Number.isFinite(pid) || pid <= 0) return;
    let cancelled = false;
    setFoldersState({ status: 'ready', error: '', folders: [] });
    setAssetsState({ status: 'loading', error: '', assets: [] });
    api
      .mediaListScoped(Number(siteId), { project_id: pid, limit: 500 })
      .then((res) => {
        if (cancelled) return;
        if (!res.ok) {
          setAssetsState({ status: 'error', error: res.error || 'Impossible de charger', assets: [] });
          return;
        }
        setAssetsState({ status: 'ready', error: '', assets: res.assets || [] });
      })
      .catch(() => {
        if (cancelled) return;
        setAssetsState({ status: 'error', error: 'Impossible de charger', assets: [] });
      });
    return () => {
      cancelled = true;
    };
  }, [siteId, current.projectId]);

  useEffect(() => refreshProjects(), [refreshProjects]);

  useEffect(() => {
    setSelectedIds(new Set());
    setPreview(null);
    setDragCrumbKey(null);
    setInvalidDropKey(null);
  }, [siteId, current.type, current.folderId, current.projectId, current.label]);

  useEffect(() => {
    setPath([{ type: 'root', label: 'Ressources' }]);
    setCtxMenu(null);
  }, [siteId]);

  useEffect(() => {
    if (current.type !== 'folder') return;
    if (!includeSubfolders) return;
    return refreshAllFolders();
  }, [current.type, includeSubfolders, refreshAllFolders]);

  useEffect(() => {
    if (!siteId) return;
    if (isClassicContext) return refreshClassic();
    if (isProjectsContext) {
      setFoldersState({ status: 'ready', error: '', folders: [] });
      setAssetsState({ status: 'ready', error: '', assets: [] });
      return;
    }
    if (isProjectFolderContext) return refreshProjectFolder();
    return undefined;
  }, [siteId, isClassicContext, isProjectsContext, isProjectFolderContext, refreshClassic, refreshProjectFolder]);

  function openNewFolderModal() {
    if (!isClassicContext) return;
    setModal({ type: 'new-folder', saving: false, error: '', name: '' });
  }

  async function createFolder() {
    if (!modal || modal.type !== 'new-folder') return;
    if (modal.saving) return;
    setModal((m) => (m ? { ...m, saving: true, error: '' } : m));
    const res = await api.mediaFolderCreate({
      site_id: Number(siteId),
      parent_id: current.type === 'folder' ? Number(current.folderId) : null,
      name: modal.name,
    });
    if (!res.ok) {
      setModal((m) => (m ? { ...m, saving: false, error: res.error || 'Erreur' } : m));
      return;
    }
    setModal(null);
    refreshClassic();
  }

  async function uploadFiles(files) {
    const list = Array.from(files || []).filter(Boolean);
    if (!list.length) return;
    if (!siteId) return;
    if (current.type === 'projects') return;
    if (uploading) return;
    setUploading(true);
    const payload =
      current.type === 'project'
        ? { project_id: Number(current.projectId) }
        : current.type === 'folder'
        ? { folder_id: Number(current.folderId) }
        : {};
    const res = await api.mediaUpload(Number(siteId), list, payload);
    setUploading(false);
    if (!res.ok) return;
    if (isClassicContext) refreshClassic();
    if (isProjectFolderContext) refreshProjectFolder();
  }

  async function deleteAsset(assetId) {
    const id = Number(assetId);
    if (!Number.isFinite(id) || id <= 0) return;
    const res = await api.mediaDelete({ site_id: Number(siteId), id });
    if (!res.ok) return;
    if (isClassicContext) refreshClassic();
    if (isProjectFolderContext) refreshProjectFolder();
  }

  async function convertToWebp(asset) {
    const a = asset || null;
    if (!a) return;
    if (!canConvertToWebp(a)) return;
    const res = await api.mediaToWebp({ site_id: Number(siteId), id: Number(a.id) });
    if (!res.ok) return;
    if (isClassicContext) refreshClassic();
    if (isProjectFolderContext) refreshProjectFolder();
  }

  function toggleSelected(id, { anchor = true } = {}) {
    const n = Number(id);
    if (!Number.isFinite(n) || n <= 0) return;
    setSelectedIds((prev) => {
      const next = new Set(prev || []);
      if (next.has(n)) next.delete(n);
      else next.add(n);
      return next;
    });
    if (anchor) lastSelectedRef.current = n;
  }

  function clearSelection() {
    setSelectedIds(new Set());
    setZipError('');
    lastSelectedRef.current = null;
  }

  function isNativeFileDrag(e) {
    const types = e?.dataTransfer?.types;
    if (!types) return false;
    try {
      return Array.from(types).includes('Files');
    } catch {
      return false;
    }
  }

  function beginDragAsset(e, asset) {
    if (!isClassicContext) return;
    const id = Number(asset?.id);
    if (!Number.isFinite(id) || id <= 0) return;
    const currentSelected = selectedIds instanceof Set ? selectedIds : new Set();
    const ids = currentSelected.has(id) ? Array.from(currentSelected) : [id];
    dragMoveRef.current = { ids };
    try {
      e.dataTransfer.effectAllowed = 'move';
      e.dataTransfer.setData('application/x-omniscope-media', JSON.stringify({ ids }));
      e.dataTransfer.setData('text/plain', String(id));
    } catch {
      // ignore
    }
  }

  function resolveDraggedMediaIds(e) {
    const fromRef = dragMoveRef.current?.ids;
    if (Array.isArray(fromRef) && fromRef.length) return fromRef;
    try {
      const raw = e?.dataTransfer?.getData?.('application/x-omniscope-media');
      if (raw) {
        const parsed = JSON.parse(raw);
        const ids = Array.isArray(parsed?.ids) ? parsed.ids.map((x) => Number(x)).filter((n) => Number.isFinite(n) && n > 0) : [];
        if (ids.length) return Array.from(new Set(ids));
      }
    } catch {
      // ignore
    }
    return [];
  }

  async function moveMediaToFolder(ids, folderId) {
    if (!siteId) return;
    const clean = (Array.isArray(ids) ? ids : []).map((x) => Number(x)).filter((n) => Number.isFinite(n) && n > 0);
    const uniq = Array.from(new Set(clean));
    if (!uniq.length) return;
    const res = await api.mediaMoveToFolder({
      site_id: Number(siteId),
      ids: uniq,
      folder_id: folderId == null ? 0 : Number(folderId),
    });
    if (!res.ok) return;
    if (isClassicContext) refreshClassic();
  }

  async function downloadSelection(ids) {
    const list = (Array.isArray(ids) ? ids : [])
      .map((x) => Number(x))
      .filter((n) => Number.isFinite(n) && n > 0);
    if (!list.length) return;
    setZipError('');

    if (list.length === 1) {
      const a = document.createElement('a');
      a.href = assetUrl(list[0], true);
      a.rel = 'noopener';
      a.download = '';
      document.body.appendChild(a);
      a.click();
      a.remove();
      return;
    }

    const fallbackDirectZipDownload = () => {
      const a = document.createElement('a');
      a.href = downloadZipUrl(list);
      a.rel = 'noopener';
      a.download = '';
      document.body.appendChild(a);
      a.click();
      a.remove();
    };

    setZipDownloading(true);
    let res = null;
    try {
      res = await fetch(downloadZipUrl(list), { credentials: 'include' });
    } catch {
      setZipDownloading(false);
      // Fallback utile sur certains proxys HTTP/2 (ex: ERR_HTTP2_PROTOCOL_ERROR).
      fallbackDirectZipDownload();
      return;
    }
    const contentType = (res.headers.get('content-type') || '').toLowerCase();
    if (!res.ok || contentType.includes('application/json')) {
      let data = null;
      try {
        data = await res.json();
      } catch {
        data = null;
      }
      setZipDownloading(false);
      if (res.status >= 500) {
        fallbackDirectZipDownload();
        return;
      }
      setZipError(data?.error || `Téléchargement ZIP impossible (HTTP ${res.status})`);
      return;
    }
    const blob = await res.blob();
    const cd = res.headers.get('content-disposition') || '';
    const filename = filenameFromContentDisposition(cd) || 'medias.zip';
    const a = document.createElement('a');
    const objectUrl = URL.createObjectURL(blob);
    a.href = objectUrl;
    a.rel = 'noopener';
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(objectUrl), 4000);
    setZipDownloading(false);
  }

  async function downloadFolderAsZip(folder) {
    const fid = Number(folder?.id);
    if (!Number.isFinite(fid) || fid <= 0) return;
    let allFolders = Array.isArray(allFoldersState.folders) ? allFoldersState.folders : [];
    if (!allFolders.length) {
      const fr = await api.mediaFolders(Number(siteId), { all: true });
      if (fr.ok) allFolders = Array.isArray(fr.folders) ? fr.folders : [];
    }
    const ar = await api.mediaList(Number(siteId));
    if (!ar.ok) {
      setZipError(ar.error || 'Chargement des fichiers impossible');
      return;
    }
    const allAssets = Array.isArray(ar.assets) ? ar.assets : [];
    const childrenByParent = new Map();
    for (const f of allFolders) {
      const id = Number(f?.id);
      if (!Number.isFinite(id) || id <= 0) continue;
      const pid = f?.parent_id == null ? null : Number(f.parent_id);
      if (!childrenByParent.has(pid)) childrenByParent.set(pid, []);
      childrenByParent.get(pid).push(id);
    }
    const allowed = new Set([fid]);
    const stack = [fid];
    while (stack.length) {
      const id = stack.pop();
      const kids = childrenByParent.get(id) || [];
      for (const k of kids) {
        if (allowed.has(k)) continue;
        allowed.add(k);
        stack.push(k);
      }
    }
    const ids = allAssets
      .filter((a) => a?.folder_id != null && allowed.has(Number(a.folder_id)))
      .map((a) => Number(a.id))
      .filter((n) => Number.isFinite(n) && n > 0);
    if (!ids.length) {
      setZipError('Aucun fichier dans ce dossier.');
      return;
    }
    await downloadSelection(ids);
  }

  async function renameAsset(id, name) {
    const res = await api.mediaUpdate({ site_id: Number(siteId), id: Number(id), original_name: String(name || '') });
    if (!res.ok) return res;
    if (isClassicContext) refreshClassic();
    if (isProjectFolderContext) refreshProjectFolder();
    return { ok: true };
  }

  async function renameFolder(id, name) {
    const res = await api.mediaFolderRename({ site_id: Number(siteId), id: Number(id), name: String(name || '') });
    if (!res.ok) return res;
    refreshClassic();
    return { ok: true };
  }

  function openContextMenu(e, items) {
    e.preventDefault();
    e.stopPropagation();
    setCtxMenu({ x: Number(e.clientX) || 0, y: Number(e.clientY) || 0, items: Array.isArray(items) ? items : [] });
  }

  function onDragEnter(e) {
    e.preventDefault();
    e.stopPropagation();
    if (current.type === 'projects') return;
    if (!isNativeFileDrag(e)) return;
    dragCounter.current += 1;
    setDragActive(true);
  }

  function onDragOver(e) {
    e.preventDefault();
    e.stopPropagation();
    if (current.type === 'projects') return;
    if (!isNativeFileDrag(e)) return;
    if (!dragActive) setDragActive(true);
  }

  function onDragLeave(e) {
    e.preventDefault();
    e.stopPropagation();
    if (current.type === 'projects') return;
    if (!isNativeFileDrag(e)) return;
    dragCounter.current -= 1;
    if (dragCounter.current <= 0) {
      dragCounter.current = 0;
      setDragActive(false);
    }
  }

  function onDrop(e) {
    e.preventDefault();
    e.stopPropagation();
    dragCounter.current = 0;
    setDragActive(false);
    const dt = e.dataTransfer;
    if (!dt) return;
    if (current.type === 'projects') return;
    if (!isNativeFileDrag(e)) return;
    uploadFiles(dt.files);
  }

  const showNewFolder = isClassicContext;
  const canUploadHere = current.type !== 'projects';
  const pageError = useMemo(() => {
    if (current.type === 'projects') return projectsState.error || '';
    if (isClassicContext) return foldersState.error || assetsState.error || '';
    if (isProjectFolderContext) return assetsState.error || '';
    return '';
  }, [current.type, projectsState.error, isClassicContext, foldersState.error, assetsState.error, isProjectFolderContext]);

  const headerActions = (
    <div className="Badges ResourcesHeaderActions">
      <input
        ref={fileInputRef}
        type="file"
        multiple
        style={{ display: 'none' }}
        onChange={(e) => {
          uploadFiles(e.target.files);
          e.target.value = '';
        }}
      />

      {showNewFolder ? (
        <button type="button" className="Button Button--ghost" onClick={openNewFolderModal}>
          <span className="ButtonInline">
            <IconFolder /> Nouveau dossier
          </span>
        </button>
      ) : null}

      {isProjectsContext ? (
        <button
          type="button"
          className="Button Button--ghost"
          onClick={() =>
            setModal({
              type: 'projects-manage',
              creating: false,
              name: '',
              error: '',
            })
          }
        >
          <span className="ButtonInline">
            <IconSparkles /> Gestion des projets
          </span>
        </button>
      ) : null}

      <button type="button" className="Button" disabled={uploading || !canUploadHere} onClick={() => fileInputRef.current?.click?.()}>
        <span className="ButtonInline">
          <IconPlus /> {uploading ? 'Upload…' : 'Uploader'}
        </span>
      </button>
    </div>
  );

  const selectionCount = selectedIds instanceof Set ? selectedIds.size : 0;
  const selectedAssets = useMemo(() => {
    const set = selectedIds instanceof Set ? selectedIds : new Set();
    return (assets || []).filter((a) => set.has(Number(a?.id)));
  }, [assets, selectedIds]);

  const anySelectedWebpConvertible = useMemo(() => selectedAssets.some((a) => canConvertToWebp(a)), [selectedAssets]);

  const visibleAssets = useMemo(() => {
    if (current.type !== 'folder' || !includeSubfolders) return assets;
    const allFolders = Array.isArray(allFoldersState.folders) ? allFoldersState.folders : [];
    const childrenByParent = new Map();
    for (const f of allFolders) {
      const pid = f?.parent_id == null ? null : Number(f.parent_id);
      const id = Number(f?.id);
      if (!Number.isFinite(id) || id <= 0) continue;
      if (!childrenByParent.has(pid)) childrenByParent.set(pid, []);
      childrenByParent.get(pid).push(id);
    }
    const rootId = Number(current.folderId);
    if (!Number.isFinite(rootId) || rootId <= 0) return assets;
    const allowed = new Set([rootId]);
    const stack = [rootId];
    while (stack.length) {
      const id = stack.pop();
      const kids = childrenByParent.get(id) || [];
      for (const k of kids) {
        if (allowed.has(k)) continue;
        allowed.add(k);
        stack.push(k);
      }
    }
    return (assets || []).filter((a) => a?.folder_id != null && allowed.has(Number(a.folder_id)));
  }, [assets, allFoldersState.folders, current.type, current.folderId, includeSubfolders]);

  const combinedEntries = useMemo(() => {
    const out = [];
    if (current.type === 'root') {
      for (const sf of rootSpecialFolders) out.push({ type: 'special', special: sf });
    }
    if (isProjectsContext) {
      for (const p of projects) out.push({ type: 'project', project: p });
      return out;
    }
    if (isClassicContext) {
      for (const f of folders) out.push({ type: 'folder', folder: f });
      for (const a of visibleAssets) out.push({ type: 'asset', asset: a });
      return out;
    }
    if (isProjectFolderContext) {
      for (const a of assets) out.push({ type: 'asset', asset: a });
      return out;
    }
    return out;
  }, [current.type, rootSpecialFolders, isProjectsContext, projects, isClassicContext, folders, visibleAssets, assets, isProjectFolderContext]);

  function entryName(entry) {
    if (!entry) return '';
    if (entry.type === 'folder') return String(entry.folder?.name || '').trim();
    if (entry.type === 'project') return String(entry.project?.name || '').trim();
    if (entry.type === 'special') return String(entry.special?.label || '').trim();
    if (entry.type === 'asset') return String(entry.asset?.original_name || '').trim();
    return '';
  }

  function entrySize(entry) {
    if (entry?.type !== 'asset') return 0;
    const n = Number(entry.asset?.size_bytes || 0);
    return Number.isFinite(n) ? n : 0;
  }

  function entryDims(entry) {
    if (entry?.type !== 'asset') return 0;
    const w = Number(entry.asset?.width || 0);
    const h = Number(entry.asset?.height || 0);
    if (!Number.isFinite(w) || !Number.isFinite(h)) return 0;
    return Math.max(0, w) * Math.max(0, h);
  }

  function isFolderLike(entry) {
    return entry?.type === 'folder' || entry?.type === 'project' || entry?.type === 'special';
  }

  const filteredEntries = useMemo(() => {
    const q = String(searchQuery || '').trim().toLowerCase();
    const base = (combinedEntries || []).filter((entry) => {
      if (!entry) return false;
      if (typeFilter === 'folders') {
        if (!isFolderLike(entry)) return false;
      } else if (typeFilter === 'files') {
        if (entry.type !== 'asset') return false;
      } else if (typeFilter === 'images') {
        if (entry.type !== 'asset' || String(entry.asset?.kind || '') !== 'image') return false;
      } else if (typeFilter === 'videos') {
        if (entry.type !== 'asset' || String(entry.asset?.kind || '') !== 'video') return false;
      }
      if (!q) return true;
      return entryName(entry).toLowerCase().includes(q);
    });

    const sortFn = (a, b) => {
      if (sortKey === 'name_desc') return entryName(b).localeCompare(entryName(a), 'fr');
      if (sortKey === 'size_desc') return entrySize(b) - entrySize(a);
      if (sortKey === 'size_asc') return entrySize(a) - entrySize(b);
      if (sortKey === 'dims_desc') return entryDims(b) - entryDims(a);
      return entryName(a).localeCompare(entryName(b), 'fr');
    };

    if (mixMode === 'mixed') return [...base].sort(sortFn);

    const foldersFirst = base.filter((entry) => isFolderLike(entry)).sort(sortFn);
    const filesNext = base.filter((entry) => !isFolderLike(entry)).sort(sortFn);
    return [...foldersFirst, ...filesNext];
  }, [combinedEntries, searchQuery, typeFilter, mixMode, sortKey]);

  const visibleAssetIds = useMemo(
    () =>
      filteredEntries
        .filter((entry) => entry?.type === 'asset')
        .map((entry) => Number(entry?.asset?.id))
        .filter((n) => Number.isFinite(n) && n > 0),
    [filteredEntries]
  );

  function handleAssetTileClick(e, asset) {
    const id = Number(asset?.id);
    if (!Number.isFinite(id) || id <= 0) return;
    const hasModifier = Boolean(e?.shiftKey || e?.ctrlKey || e?.metaKey);
    if (e?.shiftKey && Number.isFinite(Number(lastSelectedRef.current))) {
      const from = visibleAssetIds.indexOf(Number(lastSelectedRef.current));
      const to = visibleAssetIds.indexOf(id);
      if (from >= 0 && to >= 0) {
        const [a, b] = from < to ? [from, to] : [to, from];
        const range = visibleAssetIds.slice(a, b + 1);
        setSelectedIds((prev) => {
          const next = new Set(prev || []);
          range.forEach((x) => next.add(x));
          return next;
        });
        return;
      }
    }
    if (e?.shiftKey) {
      toggleSelected(id);
      return;
    }
    if (e?.ctrlKey || e?.metaKey || selectionCount > 0) {
      toggleSelected(id);
      return;
    }
    if (!hasModifier) {
      setPreview({ asset });
      lastSelectedRef.current = id;
    }
  }

  return (
    <div className="Page" onDragEnter={onDragEnter} onDragOver={onDragOver} onDragLeave={onDragLeave} onDrop={onDrop}>
      {dragActive ? <div className="DropOverlay">Dépose tes fichiers pour les uploader ici</div> : null}

      <header className="PageHeader">
        <div className="PageHeaderBar">
          <Breadcrumb items={breadcrumbItems} />
          {headerActions}
        </div>
      </header>

      <div className="ProjectsToolbar" style={{ marginBottom: 12 }}>
        <div className="ProjectsToolbarSearch Field" style={{ marginBottom: 0 }}>
          <label className="SrOnly" htmlFor="resources-search">
            Rechercher
          </label>
          <input
            id="resources-search"
            className="Input"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Rechercher un dossier ou un fichier…"
          />
        </div>
        <div className="ProjectsToolbarSort Field" style={{ marginBottom: 0 }}>
          <label className="SrOnly" htmlFor="resources-type-filter">
            Filtre
          </label>
          <select id="resources-type-filter" className="Select" value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)}>
            <option value="all">Tout</option>
            <option value="folders">Dossiers</option>
            <option value="files">Fichiers</option>
            <option value="images">Images</option>
            <option value="videos">Vidéos</option>
          </select>
        </div>
        <div className="ProjectsToolbarSort Field" style={{ marginBottom: 0 }}>
          <label className="SrOnly" htmlFor="resources-mix">
            Organisation
          </label>
          <select id="resources-mix" className="Select" value={mixMode} onChange={(e) => setMixMode(e.target.value)}>
            <option value="mixed">Dossiers/Fichiers mélangés</option>
            <option value="folders_first">Dossiers puis fichiers</option>
          </select>
        </div>
        <div className="ProjectsToolbarSort Field" style={{ marginBottom: 0 }}>
          <label className="SrOnly" htmlFor="resources-sort">
            Tri
          </label>
          <select id="resources-sort" className="Select" value={sortKey} onChange={(e) => setSortKey(e.target.value)}>
            <option value="name_asc">Nom (A → Z)</option>
            <option value="name_desc">Nom (Z → A)</option>
            <option value="size_desc">Poids (décroissant)</option>
            <option value="size_asc">Poids (croissant)</option>
            <option value="dims_desc">Dimensions (grandes d’abord)</option>
          </select>
        </div>
      </div>

      {pageError ? (
        <div className="Card" style={{ marginBottom: 14 }}>
          <div className="Error" role="alert">
            {pageError}
          </div>
        </div>
      ) : null}

      {selectionCount > 0 ? (
        <div className="BulkBar">
          <div className="Card BulkBarCard">
            <div className="Row" style={{ marginBottom: 0 }}>
              <div className="TitleSm" style={{ margin: 0 }}>
                {selectionCount} sélectionné{selectionCount > 1 ? 's' : ''}
              </div>
              <div className="Badges">
                <button
                  type="button"
                  className="Button Button--ghost"
                  onClick={() => downloadSelection(Array.from(selectedIds || []))}
                  disabled={zipDownloading}
                  title={selectionCount > 1 ? 'Télécharger la sélection en .zip' : 'Télécharger'}
                >
                  <span className="ButtonInline">
                    <IconDownload /> {zipDownloading ? 'Téléchargement…' : selectionCount > 1 ? 'Télécharger (.zip)' : 'Télécharger'}
                  </span>
                </button>
                {anySelectedWebpConvertible ? (
                  <button
                    type="button"
                    className="Button Button--ghost Button--webp"
                    onClick={async () => {
                      const items = selectedAssets.filter((a) => canConvertToWebp(a));
                      for (const a of items) {
                        // eslint-disable-next-line no-await-in-loop
                        await convertToWebp(a);
                      }
                    }}
                  >
                    <span className="ButtonInline">
                      <IconCheck /> WebP
                    </span>
                  </button>
                ) : null}
                <button
                  type="button"
                  className="Button Button--ghost Button--danger"
                  onClick={async () => {
                    const ids = Array.from(selectedIds || []);
                    for (const id of ids) {
                      // eslint-disable-next-line no-await-in-loop
                      await deleteAsset(id);
                    }
                    clearSelection();
                  }}
                >
                  <span className="ButtonInline">
                    <IconTrash /> Supprimer
                  </span>
                </button>
                <button type="button" className="Button Button--ghost" onClick={clearSelection}>
                  Annuler
                </button>
              </div>
            </div>
            {zipError ? (
              <div className="Error" role="alert" style={{ marginTop: 10 }}>
                {zipError}
              </div>
            ) : null}
          </div>
        </div>
      ) : null}

      {current.type === 'projects' && projectsState.status === 'loading' ? (
        <div className="ExplorerGrid ExplorerGrid--files">
          {Array.from({ length: 10 }).map((_, i) => (
            <div key={i} className="Tile">
              <Skeleton height={180} />
              <div style={{ marginTop: 10 }}>
                <Skeleton height={14} />
              </div>
            </div>
          ))}
        </div>
      ) : foldersState.status === 'loading' || assetsState.status === 'loading' ? (
        <div className="ExplorerGrid ExplorerGrid--files">
          {Array.from({ length: 10 }).map((_, i) => (
            <div key={i} className="Tile">
              <Skeleton height={180} />
              <div style={{ marginTop: 10 }}>
                <Skeleton height={14} />
              </div>
            </div>
          ))}
        </div>
      ) : filteredEntries.length === 0 ? (
        <div className="Muted">Aucun élément.</div>
      ) : (
        <div className="ExplorerGrid ExplorerGrid--files">
          {filteredEntries.map((entry) => {
              if (entry.type === 'special') {
                const sf = entry.special;
                const accent = moduleColor(sf.type);
                const tileKey = `tile_special_${sf.type}`;
                return (
                  <div
                    key={`special_${sf.type}`}
                    className={`Tile Tile--accent ${invalidDropKey === tileKey ? 'is-drop-invalid' : ''}`}
                    style={{ '--accent': accent }}
                    onDragOver={(e) => {
                      const ids = resolveDraggedMediaIds(e);
                      if (!ids.length) return;
                      e.preventDefault();
                      e.stopPropagation();
                      try {
                        e.dataTransfer.dropEffect = 'none';
                      } catch {
                        // ignore
                      }
                      triggerInvalidDrop(tileKey);
                    }}
                    onDrop={(e) => {
                      const ids = resolveDraggedMediaIds(e);
                      if (!ids.length) return;
                      e.preventDefault();
                      e.stopPropagation();
                      triggerInvalidDrop(tileKey);
                    }}
                  >
                    <button type="button" className="TileButton" onClick={() => setPath((p) => [...p, { type: sf.type, label: sf.label }])}>
                      <div className="MediaPreviewButton">
                        <div className="MediaThumbWrap">
                          <div className="MediaThumbPlaceholder MediaThumbPlaceholder--folder" aria-hidden="true">
                            <span className="FolderBubble">
                              <IconFolder />
                            </span>
                          </div>
                        </div>
                      </div>
                      <div className="ResourceNameLine" style={{ marginTop: 10 }}>
                        <div className="ResourceNameButtonWrap">
                          <span className="ResourceNameButton" aria-hidden="true">
                            {sf.label}
                          </span>
                        </div>
                      </div>
                    </button>
                  </div>
                );
              }

              if (entry.type === 'project') {
                const p = entry.project;
                const tileKey = `tile_project_${Number(p?.id) || ''}`;
                return (
                  <div
                    key={`project_${p.id}`}
                    className={`Tile Tile--accent ${invalidDropKey === tileKey ? 'is-drop-invalid' : ''}`}
                    style={{ '--accent': moduleColor('projects') }}
                    onDragOver={(e) => {
                      const ids = resolveDraggedMediaIds(e);
                      if (!ids.length) return;
                      e.preventDefault();
                      e.stopPropagation();
                      try {
                        e.dataTransfer.dropEffect = 'none';
                      } catch {
                        // ignore
                      }
                      triggerInvalidDrop(tileKey);
                    }}
                    onDrop={(e) => {
                      const ids = resolveDraggedMediaIds(e);
                      if (!ids.length) return;
                      e.preventDefault();
                      e.stopPropagation();
                      triggerInvalidDrop(tileKey);
                    }}
                    onContextMenu={(e) =>
                      openContextMenu(e, [
                        {
                          key: 'open',
                          label: 'Ouvrir',
                          onClick: () =>
                            setPath((prev) => [prev[0], { type: 'projects', label: 'Projets' }, { type: 'project', label: p.name || `Projet #${p.id}`, projectId: Number(p.id) }]),
                        },
                      ])
                    }
                  >
                    <button
                      type="button"
                      className="TileButton"
                      onClick={() =>
                        setPath((prev) => [
                          prev[0],
                          { type: 'projects', label: 'Projets' },
                          { type: 'project', label: p.name || `Projet #${p.id}`, projectId: Number(p.id) },
                      ])
                    }
                    >
                      <div className="MediaPreviewButton">
                        <div className="MediaThumbWrap">
                          <div className="MediaThumbPlaceholder MediaThumbPlaceholder--folder" aria-hidden="true">
                            <span className="FolderBubble">
                              <IconFolder />
                            </span>
                          </div>
                        </div>
                      </div>
                      <div className="ResourceNameLine" style={{ marginTop: 10 }}>
                        <div className="ResourceNameButtonWrap">
                          <span className="ResourceNameButton" aria-hidden="true">
                            {p.name || `Projet #${p.id}`}
                          </span>
                        </div>
                      </div>
                    </button>
                  </div>
                );
              }

              if (entry.type === 'folder') {
                const f = entry.folder;
                const accent = folderAccent(f.name);
                const tileKey = `tile_folder_${Number(f?.id) || ''}`;
                return (
                  <div
                    key={`folder_${f.id}`}
                    className={`Tile ${accent ? 'Tile--accent' : ''} ${dropFolderId === Number(f.id) ? 'is-drop-target' : ''} ${
                      invalidDropKey === tileKey ? 'is-drop-invalid' : ''
                    }`}
                    style={accent ? { '--accent': accent } : undefined}
                    onDragOver={(e) => {
                      if (!isClassicContext) return;
                      const ids = resolveDraggedMediaIds(e);
                      if (!ids.length) return;
                      e.preventDefault();
                      setDropFolderId(Number(f.id));
                      setDragCrumbKey(null);
                      try {
                        e.dataTransfer.dropEffect = 'move';
                      } catch {
                        // ignore
                      }
                    }}
                    onDragLeave={() => {
                      if (dropFolderId === Number(f.id)) setDropFolderId(null);
                    }}
                    onDrop={async (e) => {
                      if (!isClassicContext) return;
                      const ids = resolveDraggedMediaIds(e);
                      if (!ids.length) return;
                      e.preventDefault();
                      e.stopPropagation();
                      setDropFolderId(null);
                      setDragCrumbKey(null);
                      dragMoveRef.current = null;
                      await moveMediaToFolder(ids, Number(f.id));
                      clearSelection();
                    }}
                    onContextMenu={(e) =>
                      openContextMenu(e, [
                        {
                          key: 'download',
                          label: 'Télécharger le dossier (.zip)',
                          onClick: () => downloadFolderAsZip(f),
                        },
                        { type: 'sep' },
                        {
                          key: 'rename',
                          label: 'Renommer',
                          onClick: () => setModal({ type: 'rename-folder', id: Number(f.id), name: String(f.name || ''), saving: false, error: '' }),
                        },
                        {
                          key: 'delete',
                          label: 'Supprimer',
                          variant: 'danger',
                          onClick: () => setModal({ type: 'delete-folder', id: Number(f.id), name: String(f.name || ''), saving: false, error: '' }),
                        },
                      ])
                    }
                  >
                    <button
                      type="button"
                      className="TileButton"
                      onClick={() => setPath((p) => [...p, { type: 'folder', label: f.name || 'Dossier', folderId: Number(f.id) }])}
                    >
                      <div className="MediaPreviewButton">
                        <div className="MediaThumbWrap">
                          <div className="MediaThumbPlaceholder MediaThumbPlaceholder--folder" aria-hidden="true">
                            <span className="FolderBubble">
                              <IconFolder />
                            </span>
                          </div>
                        </div>
                      </div>
                      <div className="ResourceNameLine" style={{ marginTop: 10 }}>
                        <div className="ResourceNameButtonWrap">
                          <span className="ResourceNameButton" aria-hidden="true">
                            {f.name || 'Dossier'}
                          </span>
                        </div>
                      </div>
                    </button>
                  </div>
                );
              }

              const a = entry.asset;
              const isImage = a.kind === 'image';
              const isVideo = a.kind === 'video';
              const isSelected = selectedIds instanceof Set ? selectedIds.has(Number(a.id)) : false;
              const thumb = isImage ? (
                <MediaThumbImage src={assetUrl(a.id, false)} alt={a.note || a.original_name || ''} eager={preloadImages} />
              ) : isVideo ? (
                <MediaThumbVideoPlaceholder />
              ) : (
                <div className="MediaThumbWrap">
                  <div className="MediaThumbPlaceholder" aria-hidden="true">
                    <IconImage />
                  </div>
                </div>
              );
              const dims = isImage && a.width && a.height ? `${a.width}×${a.height}` : '';
              return (
                <div
                  key={`asset_${a.id}`}
                  className={`Tile ${isSelected ? 'is-selected' : ''}`}
                  onContextMenu={(e) =>
                    openContextMenu(
                      e,
                      [
                        {
                          key: 'rename',
                          label: 'Renommer',
                          onClick: () => setModal({ type: 'rename-asset', id: Number(a.id), name: String(a.original_name || ''), saving: false, error: '' }),
                        },
                        {
                          key: 'download',
                          label: 'Télécharger',
                          onClick: () => downloadSelection([Number(a.id)]),
                        },
                        isImage && canConvertToWebp(a)
                          ? {
                              key: 'webp',
                              label: 'Convertir en WebP',
                              onClick: () => convertToWebp(a),
                            }
                          : null,
                        { type: 'sep' },
                        {
                          key: 'delete',
                          label: 'Supprimer',
                          variant: 'danger',
                          onClick: () => setModal({ type: 'delete-asset', id: Number(a.id), name: String(a.original_name || ''), saving: false, error: '' }),
                        },
                      ].filter(Boolean)
                    )
                  }
                >
                  <button
                    type="button"
                    className={`SelectToggle SelectToggle--radio ${isSelected ? 'is-on' : ''}`}
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      toggleSelected(a.id);
                    }}
                    title={isSelected ? 'Désélectionner' : 'Sélectionner'}
                    aria-label={isSelected ? 'Désélectionner' : 'Sélectionner'}
                  >
                    <span className="RadioMark" aria-hidden="true" />
                  </button>
                  <button
                    type="button"
                    className="TileButton"
                    onClick={(e) => handleAssetTileClick(e, a)}
                    draggable={isClassicContext}
                    onDragStart={(e) => beginDragAsset(e, a)}
                    onDragEnd={() => {
                      dragMoveRef.current = null;
                      setDropFolderId(null);
                      setDragCrumbKey(null);
                      setInvalidDropKey(null);
                    }}
                  >
                    <div className="MediaPreviewButton">{thumb}</div>
                    <div className="ResourceNameLine">
                      <div className="ResourceNameButtonWrap">
                        <span className="ResourceNameButton" aria-hidden="true">
                          {a.original_name || `Media #${a.id}`}
                        </span>
                      </div>
                    </div>
                    <div className="Muted" style={{ fontSize: 12, marginTop: 6 }}>
                      {formatBytes(a.size_bytes)}
                      {dims ? ` · ${dims}` : ''} · {a.kind}
                    </div>
                  </button>
                </div>
              );
            })}
        </div>
      )}

      {modal?.type === 'new-folder' ? (
        <Modal
          title="Nouveau dossier"
          icon={<IconFolder />}
          onClose={() => setModal(null)}
          submitOnEnter
          onSubmit={createFolder}
          footer={
            <>
              <button type="button" className="Button Button--ghost" onClick={() => setModal(null)}>
                Annuler
              </button>
              <button type="button" className="Button" onClick={createFolder} disabled={modal.saving}>
                {modal.saving ? 'Création…' : 'Créer'}
              </button>
            </>
          }
        >
          <div className="Field">
            <label className="Label" htmlFor="folder-name">
              Nom
            </label>
            <input
              id="folder-name"
              className="Input"
              value={modal.name}
              onChange={(e) => setModal((m) => (m ? { ...m, name: e.target.value } : m))}
              placeholder="Ex: Photos, Documents…"
            />
          </div>
          {modal.error ? (
            <div className="Error" role="alert">
              {modal.error}
            </div>
          ) : null}
        </Modal>
      ) : null}

      {modal?.type === 'projects-manage' ? (
        <Modal
          title="Gestion des projets"
          icon={<IconSparkles />}
          onClose={() => setModal(null)}
          footer={
            <button type="button" className="Button Button--ghost" onClick={() => setModal(null)}>
              Fermer
            </button>
          }
        >
          <div className="Muted" style={{ marginBottom: 10 }}>
            Projets détectés: <strong>{Number(projects.length)}</strong>
          </div>
          <div className="List" role="list" style={{ marginBottom: 12, maxHeight: 220, overflow: 'auto' }}>
            {(projects || []).slice(0, 60).map((p) => (
              <div key={`manage_project_${p.id}`} className="ListItem" role="listitem">
                <span className="OneLine">{String(p.name || `Projet #${p.id}`)}</span>
              </div>
            ))}
            {(projects || []).length === 0 ? <div className="Muted">Aucun projet.</div> : null}
          </div>

          <div className="RoleBox">
            <div className="LabelStrong">Créer un projet</div>
            <div className="Field" style={{ marginTop: 10 }}>
              <label className="Label">Nom</label>
              <input
                className="Input"
                value={String(modal.name || '')}
                onChange={(e) => setModal((m) => ({ ...(m || {}), name: e.target.value }))}
                placeholder="Nom du projet"
              />
            </div>
            <div className="Badges" style={{ justifyContent: 'flex-start' }}>
              <button
                type="button"
                className="Button"
                disabled={Boolean(modal.creating)}
                onClick={async () => {
                  if (modal.creating) return;
                  const name = String(modal.name || '').trim();
                  if (!name) {
                    setModal((m) => ({ ...(m || {}), error: 'Nom requis' }));
                    return;
                  }
                  setModal((m) => ({ ...(m || {}), creating: true, error: '' }));
                  const res = await api.projectCreate({
                    site_id: Number(siteId),
                    lang: 'fr',
                    name,
                    slug: '',
                    cover_image_url: '',
                    background_image_url: '',
                    tag_ids: [],
                    category_ids: [],
                    content: defaultProjectContent(),
                  });
                  if (!res.ok) {
                    setModal((m) => ({ ...(m || {}), creating: false, error: res.error || 'Création impossible' }));
                    return;
                  }
                  await refreshProjects();
                  setModal((m) => ({ ...(m || {}), creating: false, name: '', error: '' }));
                }}
              >
                {modal.creating ? 'Création…' : 'Créer le projet'}
              </button>
            </div>
            {modal.error ? (
              <div className="Error" role="alert" style={{ marginTop: 10 }}>
                {modal.error}
              </div>
            ) : null}
          </div>
        </Modal>
      ) : null}

      {modal?.type === 'rename-asset' ? (
        <Modal
          title="Renommer le fichier"
          icon={<IconEdit />}
          onClose={() => setModal(null)}
          submitOnEnter
          onSubmit={async () => {
            if (modal.saving) return;
            setModal((m) => ({ ...m, saving: true, error: '' }));
            const res = await renameAsset(modal.id, modal.name);
            if (!res?.ok) {
              setModal((m) => ({ ...m, saving: false, error: res?.error || 'Erreur' }));
              return;
            }
            setModal(null);
          }}
          footer={
            <>
              <button type="button" className="Button Button--ghost" onClick={() => setModal(null)} disabled={modal.saving}>
                Annuler
              </button>
              <button type="button" className="Button" disabled={modal.saving} onClick={async () => {
                setModal((m) => ({ ...m, saving: true, error: '' }));
                const res = await renameAsset(modal.id, modal.name);
                if (!res?.ok) {
                  setModal((m) => ({ ...m, saving: false, error: res?.error || 'Erreur' }));
                  return;
                }
                setModal(null);
              }}>
                {modal.saving ? 'Renommage…' : 'Renommer'}
              </button>
            </>
          }
        >
          <div className="Field">
            <label className="Label">Nom</label>
            <input className="Input" value={modal.name} onChange={(e) => setModal((m) => ({ ...m, name: e.target.value }))} />
          </div>
          {modal.error ? <div className="Error" role="alert">{modal.error}</div> : null}
        </Modal>
      ) : null}

      {modal?.type === 'rename-folder' ? (
        <Modal
          title="Renommer le dossier"
          icon={<IconEdit />}
          onClose={() => setModal(null)}
          footer={
            <>
              <button type="button" className="Button Button--ghost" onClick={() => setModal(null)} disabled={modal.saving}>
                Annuler
              </button>
              <button type="button" className="Button" disabled={modal.saving} onClick={async () => {
                setModal((m) => ({ ...m, saving: true, error: '' }));
                const res = await renameFolder(modal.id, modal.name);
                if (!res?.ok) {
                  setModal((m) => ({ ...m, saving: false, error: res?.error || 'Erreur' }));
                  return;
                }
                setModal(null);
              }}>
                {modal.saving ? 'Renommage…' : 'Renommer'}
              </button>
            </>
          }
        >
          <div className="Field">
            <label className="Label">Nom</label>
            <input className="Input" value={modal.name} onChange={(e) => setModal((m) => ({ ...m, name: e.target.value }))} />
          </div>
          {modal.error ? <div className="Error" role="alert">{modal.error}</div> : null}
        </Modal>
      ) : null}

      {modal?.type === 'delete-asset' ? (
        <Modal
          title="Supprimer le fichier"
          icon={<IconAlertTriangle />}
          onClose={() => setModal(null)}
          footer={
            <>
              <button type="button" className="Button Button--ghost" onClick={() => setModal(null)} disabled={modal.saving}>
                Annuler
              </button>
              <button type="button" className="Button Button--danger" disabled={modal.saving} onClick={async () => {
                setModal((m) => ({ ...m, saving: true, error: '' }));
                await deleteAsset(modal.id);
                setModal(null);
              }}>
                {modal.saving ? 'Suppression…' : 'Supprimer'}
              </button>
            </>
          }
        >
          <div className="Muted">Supprimer <strong>{modal.name || 'ce fichier'}</strong> ?</div>
        </Modal>
      ) : null}

      {modal?.type === 'delete-folder' ? (
        <Modal
          title="Supprimer le dossier"
          icon={<IconAlertTriangle />}
          onClose={() => setModal(null)}
          footer={
            <>
              <button type="button" className="Button Button--ghost" onClick={() => setModal(null)} disabled={modal.saving}>
                Annuler
              </button>
              <button type="button" className="Button Button--danger" disabled={modal.saving} onClick={async () => {
                setModal((m) => ({ ...m, saving: true, error: '' }));
                const res = await api.mediaFolderDelete({ site_id: Number(siteId), id: Number(modal.id) });
                if (!res.ok) {
                  setModal((m) => ({ ...m, saving: false, error: res.error || 'Erreur' }));
                  return;
                }
                setModal(null);
                refreshClassic();
              }}>
                {modal.saving ? 'Suppression…' : 'Supprimer'}
              </button>
            </>
          }
        >
          <div className="Muted">Supprimer le dossier <strong>{modal.name || ''}</strong> ?</div>
          {modal.error ? <div className="Error" role="alert" style={{ marginTop: 10 }}>{modal.error}</div> : null}
        </Modal>
      ) : null}

      {preview?.asset ? (
        <MediaPreviewOverlay
          siteId={Number(siteId)}
          asset={preview.asset}
          src={assetUrl(preview.asset.id, false)}
          canWebp={canConvertToWebp(preview.asset)}
          onClose={() => setPreview(null)}
          onDelete={async () => {
            await deleteAsset(preview.asset.id);
            setPreview(null);
          }}
          onConvert={async () => convertToWebp(preview.asset)}
          onSaveAlt={async (text) => {
            const res = await api.mediaUpdate({ site_id: Number(siteId), id: Number(preview.asset.id), note: String(text || '') });
            if (!res.ok) return false;
            setPreview((p) => (p?.asset ? { ...p, asset: { ...(p.asset || {}), note: String(text || '') } } : p));
            if (isClassicContext) refreshClassic();
            if (isProjectFolderContext) refreshProjectFolder();
            return true;
          }}
        />
      ) : null}

      <FixedContextMenu menu={ctxMenu} onClose={() => setCtxMenu(null)} />
    </div>
  );
}

function MediaPreviewOverlay({ siteId, asset, src, canWebp, onClose, onDelete, onConvert, onSaveAlt }) {
  const [altOpen, setAltOpen] = useState(false);
  const [altDraft, setAltDraft] = useState(String(asset?.note || ''));
  const [savingAlt, setSavingAlt] = useState(false);

  useEffect(() => {
    setAltDraft(String(asset?.note || ''));
  }, [asset?.id, asset?.note]);

  useEffect(() => {
    function onKey(e) {
      if (e.key === 'Escape') onClose?.();
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  useEffect(() => {
    const body = document.body;
    const prevOverflow = body.style.overflow;
    body.style.overflow = 'hidden';
    return () => {
      body.style.overflow = prevOverflow;
    };
  }, []);

  async function saveAlt() {
    if (savingAlt) return;
    setSavingAlt(true);
    const ok = await onSaveAlt?.(altDraft);
    setSavingAlt(false);
    if (ok) setAltOpen(false);
  }

  if (typeof document === 'undefined') return null;

  const isImage = asset?.kind === 'image';
  const isVideo = asset?.kind === 'video';

  return createPortal(
    <div className="PreviewOverlay" role="presentation" onMouseDown={onClose}>
      <div
        className="PreviewContent PreviewContent--withSide"
        onMouseDown={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-label={asset?.original_name || 'Prévisualisation'}
      >
        <button type="button" className="PreviewClose Button Button--ghost" onClick={onClose}>
          Fermer
        </button>

        <div className="PreviewMain">
          {isImage ? <img src={src} alt={asset?.note || asset?.original_name || ''} className="PreviewMedia" decoding="async" /> : null}
          {isVideo ? <video src={src} className="PreviewMedia" controls playsInline preload="metadata" /> : null}
          {!isImage && !isVideo ? (
            <div className="Card" style={{ width: 'min(720px, 100%)', background: 'transparent', border: 0, boxShadow: 'none' }}>
              <div className="TitleMd">{asset?.original_name || `Media #${asset?.id || ''}`}</div>
              <div className="Muted" style={{ marginTop: 6 }}>
                Type: <code>{asset?.mime_type || asset?.kind || '—'}</code>
              </div>
            </div>
          ) : null}
        </div>

        <aside className="PreviewSide" aria-label="Actions">
          <div className="LabelStrong">Actions</div>
          <div className={`Badges PreviewActions ${isImage ? 'PreviewActions--col' : ''}`} style={{ justifyContent: 'flex-start', marginTop: 10 }}>
            <a className="Button Button--ghost" href={`${src}&download=1`} target="_blank" rel="noreferrer">
              <span className="ButtonInline">
                <IconDownload /> Télécharger
              </span>
            </a>
            <button type="button" className="Button Button--ghost" onClick={() => setAltOpen((v) => !v)}>
              <span className="ButtonInline">
                <IconEdit /> Texte alternatif
              </span>
            </button>
            {canWebp ? (
              <button type="button" className="Button Button--ghost Button--webp" onClick={onConvert}>
                <span className="ButtonInline">
                  <IconSparkles /> Convertir en WebP
                </span>
              </button>
            ) : null}
            <button type="button" className="Button Button--ghost Button--danger" onClick={onDelete}>
              <span className="ButtonInline">
                <IconTrash /> Supprimer
              </span>
            </button>
          </div>

          {altOpen ? (
            <div className="RoleBox AltBox" style={{ marginTop: 12 }}>
              <div className="LabelStrong">Texte alternatif</div>
              <textarea
                className="Textarea AltTextarea"
                rows={5}
                value={altDraft}
                onChange={(e) => setAltDraft(e.target.value)}
                placeholder="Décris l’image pour l’accessibilité…"
              />
              <div className="Badges" style={{ justifyContent: 'flex-start', marginTop: 10 }}>
                <button type="button" className="Button" onClick={saveAlt} disabled={savingAlt}>
                  <span className="ButtonInline">
                    <IconCheck /> {savingAlt ? 'Sauvegarde…' : 'Sauvegarder'}
                  </span>
                </button>
              </div>
            </div>
          ) : null}
        </aside>
      </div>
    </div>,
    document.body
  );
}
